import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Settings, Save, TrendingUp, Percent, Users, UserPlus, Loader2, AlertTriangle } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import InvestorManagement from '@/components/InvestorManagement';
import CreateInvestorAccounts from '@/components/CreateInvestorAccounts';

interface OperationSetting {
  id: string;
  monthly_rate: number;
  reinvestment_rate: number;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

const AdminPanel = () => {
  const [settings, setSettings] = useState<OperationSetting | null>(null);
  const [monthlyRate, setMonthlyRate] = useState('');
  const [reinvestmentRate, setReinvestmentRate] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // User creation form state
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    displayName: ''
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  
  const { toast } = useToast();

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('operation_settings')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        toast({
          title: "Erro ao carregar configurações",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      if (data) {
        setSettings(data);
        setMonthlyRate((data.monthly_rate * 100).toFixed(2));
        setReinvestmentRate((data.reinvestment_rate * 100).toFixed(2));
      } else {
        // No settings found, use defaults
        setMonthlyRate('1.50');
        setReinvestmentRate('1.20');
      }
    } catch (error) {
      // Handle error silently
    } finally {
      setLoading(false);
    }
  };

  const validateUserForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!userForm.email) {
      errors.email = 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(userForm.email)) {
      errors.email = 'Email inválido';
    }
    
    if (!userForm.password) {
      errors.password = 'Senha é obrigatória';
    } else if (userForm.password.length < 8) {
      errors.password = 'Senha deve ter pelo menos 8 caracteres';
    }
    
    if (!userForm.displayName) {
      errors.displayName = 'Nome é obrigatório';
    } else if (userForm.displayName.length < 2) {
      errors.displayName = 'Nome deve ter pelo menos 2 caracteres';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateUserForm()) {
      return;
    }
    
    setCreatingUser(true);
    
    try {
      const { error } = await supabase.auth.signUp({
        email: userForm.email,
        password: userForm.password,
        options: {
          data: {
            display_name: userForm.displayName
          }
        }
      });
      
      if (error) {
        toast({
          title: "Erro ao criar usuário",
          description: error.message || "Ocorreu um erro ao criar o usuário",
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: "Usuário criado com sucesso!",
        description: `A conta para ${userForm.displayName} foi criada.`,
      });
      
      // Reset form
      setUserForm({ email: '', password: '', displayName: '' });
      setFormErrors({});
      
    } catch (error) {
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro ao criar o usuário.",
        variant: "destructive",
      });
    } finally {
      setCreatingUser(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    
    try {
      const monthlyDecimal = parseFloat(monthlyRate) / 100;
      const reinvestmentDecimal = parseFloat(reinvestmentRate) / 100;

      if (isNaN(monthlyDecimal) || isNaN(reinvestmentDecimal)) {
        toast({
          title: "Erro nos valores",
          description: "Por favor, insira valores numéricos válidos.",
          variant: "destructive",
        });
        setSaving(false);
        return;
      }

      // Deactivate current settings
      if (settings) {
        await supabase
          .from('operation_settings')
          .update({ is_active: false })
          .eq('id', settings.id);
      }

      // Create new active settings
      const { error } = await supabase
        .from('operation_settings')
        .insert({
          monthly_rate: monthlyDecimal,
          reinvestment_rate: reinvestmentDecimal,
          created_by: (await supabase.auth.getUser()).data.user?.id,
          is_active: true
        });

      if (error) {
        toast({
          title: "Erro ao salvar configurações",
          description: error.message,
          variant: "destructive",
        });
        return;
      }

      toast({
        title: "Configurações atualizadas!",
        description: "As novas taxas foram aplicadas com sucesso.",
      });

      // Refresh settings
      await fetchSettings();
    } catch (error) {
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro ao salvar as configurações.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-32 bg-muted animate-pulse rounded-lg" />
        <div className="h-64 bg-muted animate-pulse rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 bg-gradient-primary rounded-lg shadow-glow">
          <Settings className="h-6 w-6 text-primary-foreground" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-foreground">Painel Administrativo</h2>
          <p className="text-muted-foreground">Gerencie investidores e configure operações</p>
        </div>
      </div>

      {/* Admin Tabs */}
      <Tabs defaultValue="investors" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="investors" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Investidores
          </TabsTrigger>
          <TabsTrigger value="create-accounts" className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            Criar Contas
          </TabsTrigger>
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Criar Usuário
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Configurações
          </TabsTrigger>
        </TabsList>

        <TabsContent value="investors" className="space-y-6">
          <InvestorManagement />
        </TabsContent>

        <TabsContent value="create-accounts" className="space-y-6">
          <CreateInvestorAccounts />
        </TabsContent>

        <TabsContent value="users" className="space-y-6">
          <Card className="border-0 shadow-soft bg-card/80 backdrop-blur-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <UserPlus className="h-5 w-5" />
                    Criar Novo Usuário
                  </CardTitle>
                  <CardDescription>
                    Crie uma nova conta de investidor no sistema
                  </CardDescription>
                </div>
                <Badge variant="secondary" className="bg-success/10 text-success">
                  Administrador
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={createUser} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="user-name">Nome Completo</Label>
                  <Input
                    id="user-name"
                    type="text"
                    placeholder="Nome completo do investidor"
                    value={userForm.displayName}
                    onChange={(e) => {
                      setUserForm({ ...userForm, displayName: e.target.value });
                      if (formErrors.displayName) {
                        setFormErrors({ ...formErrors, displayName: '' });
                      }
                    }}
                    className={formErrors.displayName ? 'border-destructive' : ''}
                    required
                  />
                  {formErrors.displayName && (
                    <div className="flex items-center gap-2 text-sm text-destructive">
                      <AlertTriangle className="h-4 w-4" />
                      {formErrors.displayName}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="user-email">Email</Label>
                  <Input
                    id="user-email"
                    type="email"
                    placeholder="email@exemplo.com"
                    value={userForm.email}
                    onChange={(e) => {
                      setUserForm({ ...userForm, email: e.target.value });
                      if (formErrors.email) {
                        setFormErrors({ ...formErrors, email: '' });
                      }
                    }}
                    className={formErrors.email ? 'border-destructive' : ''}
                    required
                  />
                  {formErrors.email && (
                    <div className="flex items-center gap-2 text-sm text-destructive">
                      <AlertTriangle className="h-4 w-4" />
                      {formErrors.email}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="user-password">Senha Inicial</Label>
                  <Input
                    id="user-password"
                    type="password"
                    placeholder="••••••••"
                    value={userForm.password}
                    onChange={(e) => {
                      setUserForm({ ...userForm, password: e.target.value });
                      if (formErrors.password) {
                        setFormErrors({ ...formErrors, password: '' });
                      }
                    }}
                    className={formErrors.password ? 'border-destructive' : ''}
                    required
                  />
                  {formErrors.password && (
                    <div className="flex items-center gap-2 text-sm text-destructive">
                      <AlertTriangle className="h-4 w-4" />
                      {formErrors.password}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    A senha deve ter pelo menos 8 caracteres
                  </p>
                </div>

                <div className="flex justify-end">
                  <Button 
                    type="submit"
                    disabled={creatingUser}
                    className="min-w-32"
                  >
                    {creatingUser ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Criando...
                      </>
                    ) : (
                      <>
                        <UserPlus className="mr-2 h-4 w-4" />
                        Criar Usuário
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6">
          {/* Current Settings Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-0 shadow-soft bg-gradient-card">
              <CardHeader className="flex flex-row items-center space-y-0 pb-2">
                <CardTitle className="text-base font-medium">Taxa Mensal Atual</CardTitle>
                <TrendingUp className="h-4 w-4 ml-auto text-success" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  {settings ? (settings.monthly_rate * 100).toFixed(2) : '1.50'}%
                </div>
                <p className="text-xs text-muted-foreground">ao mês</p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-soft bg-gradient-card">
              <CardHeader className="flex flex-row items-center space-y-0 pb-2">
                <CardTitle className="text-base font-medium">Taxa de Reinvestimento</CardTitle>
                <Percent className="h-4 w-4 ml-auto text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  {settings ? (settings.reinvestment_rate * 100).toFixed(2) : '1.20'}%
                </div>
                <p className="text-xs text-muted-foreground">ao mês</p>
              </CardContent>
            </Card>
          </div>

          {/* Settings Configuration */}
          <Card className="border-0 shadow-soft bg-card/80 backdrop-blur-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Configurar Taxas</CardTitle>
                  <CardDescription>
                    Defina as taxas mensais e de reinvestimento para as operações
                  </CardDescription>
                </div>
                <Badge variant="secondary" className="bg-success/10 text-success">
                  Administrador
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="monthly-rate">Taxa Mensal (%)</Label>
                  <Input
                    id="monthly-rate"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="1.50"
                    value={monthlyRate}
                    onChange={(e) => setMonthlyRate(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Taxa aplicada mensalmente às operações de factoring
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reinvestment-rate">Taxa de Reinvestimento (%)</Label>
                  <Input
                    id="reinvestment-rate"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="1.20"
                    value={reinvestmentRate}
                    onChange={(e) => setReinvestmentRate(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Taxa oferecida para reinvestimentos automáticos
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={saveSettings}
                  disabled={saving}
                  className="min-w-32"
                >
                  {saving ? (
                    <>
                      <Save className="mr-2 h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Salvar Configurações
                    </>
                  )}
                </Button>
              </div>

              {settings && (
                <div className="pt-4 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Última atualização: {new Date(settings.updated_at).toLocaleString('pt-BR')}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPanel;